import MemLearning from '@memrise/memlearning';
import {
  LearnableWithProgress,
  SessionState,
  TemplateName,
  TestSettings,
  WebSessionType,
} from 'src/api/learningSessions/types';
import {convertSessionTypeToMemLearning} from './conversion';
import {makeRepository} from './repository';
import {makePresenter, PresentSummary} from './presenter';
import {makeTestSettings} from './testSettings';
import {correctnessExtender} from './correctnessExtender';
import {eventManager, trackingContext} from './tracking';
import {makeSubscriber} from './subscriber';

export type SetSessionState = (state: Partial<SessionState>) => void;

const timeBasedPointsInSpeedReview = true;
const learnV2Tests = true;
const speedReviewTimerBasedInCharactersLength = false;
const supportsComprehensionTests = false;

export const createSessionStore = ({
  learnablesWithProgress,
  sessionType,
  updateSessionState,
  presentSummary,
  onMemlearningError,
  testSettings,
  forceTestType,
}: {
  learnablesWithProgress: LearnableWithProgress[];
  sessionType: WebSessionType;
  updateSessionState: SetSessionState;
  presentSummary: PresentSummary;
  onMemlearningError: (error: Error) => void;
  testSettings: TestSettings;
  forceTestType?: TemplateName | null,
}): { store: MemLearning.com.memrise.learning.session.SessionStore, startSession: () => void } => {
  const kotlinSessionType = convertSessionTypeToMemLearning(sessionType);
  const repository = makeRepository(learnablesWithProgress, forceTestType);
  const presenter = makePresenter({ setState: updateSessionState, presentSummary });
  const subscriber = makeSubscriber(updateSessionState, presentSummary, onMemlearningError);

  const memlearningTestSettings = makeTestSettings(testSettings, sessionType);
  const prioritizeTyping = testSettings.prioritizeTyping;

  const sessionConfig = new MemLearning.com.memrise.learning.session.SessionConfiguration(
    kotlinSessionType,
    trackingContext,
    memlearningTestSettings,
    // When a user enables the learning setting “typing tests” we shall set to true
    prioritizeTyping, // Session config to boost typing tests in Classic Review
    timeBasedPointsInSpeedReview,
    learnV2Tests,
    speedReviewTimerBasedInCharactersLength,
    supportsComprehensionTests,
    prioritizeTyping,
  );

  const store = MemLearning.com.memrise.learning.session.SessionStore.SessionStoreConstructor(
    sessionConfig,
    subscriber as unknown as MemLearning.com.memrise.learning.session.SessionSubscriber,
    presenter as unknown as MemLearning.com.memrise.learning.session.SessionPresenting,
    repository as unknown as MemLearning.com.memrise.learning.session.SessionRepository,
    eventManager as unknown as MemLearning.com.memrise.learning.session.events.EventManaging,
    correctnessExtender as MemLearning.com.memrise.learning.session.CorrectnessExtending, // TO-DO - fix casting for all the above
  );

  const action = new MemLearning.com.memrise.learning.session.InitializeAndStartSessionAction(repository as unknown as MemLearning.com.memrise.learning.learnables.LearnablesRepository); // TO-DO - fix casting

  return { store, startSession: () => store.dispatch(action) };
};
